import { point, distance } from '@turf/turf';
import { keyBy } from 'lodash';

import Log from 'utils/Log';
import Settings from 'utils/Settings';

import {
  STORE_LOCATION_SHOPIFY_CONTENTFUL_MAPPING,
  STORE_LOCATION_SHOPIFY_CONTENTFUL_EXCLUDED_LIST,
} from './constants';
class StoreLocationUtils {
  /**
   * Receive an array of store location items and return an object with the 'handle' as key
   *
   * @param {Array} storeLocationArray
   * @returns {Objec} an object with the 'handle' as key
   */
  static transformArrayIntoObjectByHandle(storeLocationArray) {
    if (!storeLocationArray || storeLocationArray.length == 0) {
      Log.warn(
        'StoreLocationUtils - could not transform store location array into opbject, received empty data'
      );

      return storeLocationArray;
    }

    return keyBy(storeLocationArray, 'handle');
  }

  /**
   * Map the current domain and shopify location ids with the store location content
   *
   * @param {Object} storeLocationContentObj containing the store location content by handle
   * @param {Object} storeLocationShopifyIds containing the shopify location ids for a size
   * @returns {Object} with the store location mapped and filtered by the shopify location ids received
   */
  static mapShopifyLocationIdWithContentHandle(storeLocationContentObj, storeLocationShopifyIds) {
    if (!storeLocationContentObj || !storeLocationShopifyIds) {
      Log.warn(
        'StoreLocationUtils - map shopify location Id with content handle - received empty content and locations ids'
      );

      return {};
    }

    const currentDomain = Settings?.shopPermanentDomain;
    const storeLocationContentfulMappingByDomain =
      STORE_LOCATION_SHOPIFY_CONTENTFUL_MAPPING[currentDomain];
    let storeLocationContentMapped = {};

    try {
      storeLocationContentMapped = Object.keys(storeLocationShopifyIds).reduce(
        (storeLocationContentMappedReduced, shopifyLocationId) => {
          // filter out locations excluded form the mapping
          if (
            STORE_LOCATION_SHOPIFY_CONTENTFUL_EXCLUDED_LIST.includes(parseInt(shopifyLocationId))
          ) {
            return {
              ...storeLocationContentMappedReduced,
            };
          }

          const contentfulHandle =
            storeLocationContentfulMappingByDomain[shopifyLocationId]?.contentfulHandle;
          const contentfulStoreLocation = storeLocationContentObj[contentfulHandle];
          const storeLocationInventoryLevel = storeLocationShopifyIds[shopifyLocationId];

          if (contentfulHandle && contentfulStoreLocation) {
            return {
              ...storeLocationContentMappedReduced,
              [contentfulHandle]: {
                ...contentfulStoreLocation,
                inventoryLevel: storeLocationInventoryLevel,
              },
            };
          } else {
            Log.debug(
              `StoreLocationUtils - map shopify location Id with content handle - location id '${shopifyLocationId}' or contentful handle '${contentfulHandle}' could not be mapped`
            );

            return {
              ...storeLocationContentMappedReduced,
            };
          }
        },
        {}
      );

      if (Object.keys(storeLocationContentMapped)?.length == 0) {
        throw new Error(
          'StoreLocationUtils - map shopify location Id with content handle - process result in empty object'
        );
      }
    } catch (error) {
      Log.error(
        error,
        'StoreLocationUtils - map shopify location Id with content handle - process throw error while processing data'
      );
    }

    return storeLocationContentMapped;
  }

  /**
   * Verify if it is a valid graphql shopify location id
   * ie: stock-gid://shopify/Location/67852992695 --> true
   *
   * @param {string} graphqlId graphql id
   * @returns {boolean} indicating if it is a valid graphql shopify location id
   */
  static isShopifyLocationGraphqlId(graphqlId) {
    return graphqlId && graphqlId.includes && graphqlId.includes('/shopify/Location/');
  }

  /**
   * Extract the id from a graphql string id
   * ie: stock-gid://shopify/Location/67852992695 --> 67852992695
   *
   * @param {string} graphqlId graphql id
   * @returns {number|undefined} the numeric id of the location
   */
  static getNumericIdFromGraphqlId(graphqlId) {
    const pattern = /(\/Location\/)([\d]*)$/;
    let id;

    try {
      id = parseInt(pattern.exec(graphqlId)[2]);
    } catch (error) {
      return id;
    }

    return id;
  }

  /**
   * Map store location with grapql id as keys and return same object with keys in numeric
   *
   * @param {object} storeLocationShopifyGraphqlIds with grapql id as keys
   * @returns {object} same object with numeric id as key
   */
  static mapShopifyLocationGraphqlIdToNumericId(storeLocationShopifyGraphqlIds) {
    let storeLocationShopifyIdsResult = {};
    const failingLocationIdList = [];

    if (!storeLocationShopifyGraphqlIds) {
      Log.debug(
        'StoreLocationUtils - could not map shopify graphql location ids into numeric location ids, received empty data'
      );

      return storeLocationShopifyIdsResult;
    }

    try {
      storeLocationShopifyIdsResult = Object.keys(storeLocationShopifyGraphqlIds).reduce(
        (storeLocationShopifyNumericlIds, grapqlShopifyId) => {
          let storeLocationShopifyNumericlIdsReduced = {
            ...storeLocationShopifyNumericlIds,
          };

          if (StoreLocationUtils.isShopifyLocationGraphqlId(grapqlShopifyId)) {
            const numericLoationId = StoreLocationUtils.getNumericIdFromGraphqlId(grapqlShopifyId);

            if (numericLoationId) {
              storeLocationShopifyNumericlIdsReduced = {
                ...storeLocationShopifyNumericlIds,
                [numericLoationId]: storeLocationShopifyGraphqlIds[grapqlShopifyId],
              };
            } else {
              failingLocationIdList.push(grapqlShopifyId);
            }
          }

          return storeLocationShopifyNumericlIdsReduced;
        },
        {}
      );

      if (failingLocationIdList.length > 0) {
        Log.warn(
          `StoreLocationUtils - could not extract numeric id from list ${failingLocationIdList}`
        );
      }
    } catch (error) {
      Log.error(
        error,
        'StoreLocationUtils - could not map shopify graphql location ids into numeric ids, error processing data'
      );
    }

    return storeLocationShopifyIdsResult;
  }

  static sortLocationsByDistanceFromOrigin(locations, origin) {
    const updatedShopOrder = [];
    Object.values(locations).forEach(shop => {
      if (origin?.result?.center) {
        const to = [shop?.geoLocation?.lon, shop?.geoLocation?.lat];
        const from = origin.result.center;
        var options = { units: 'miles' };

        var distanceBetweenUs = distance(from, to, options);
        updatedShopOrder.push({ handle: shop.handle, distanceBetweenUs });
      } else {
        // if there's an error calculating (invalid origin) push unsorted
        updatedShopOrder.push({ handle: shop.handle });
      }
    });

    updatedShopOrder.sort((a, b) => {
      return a.distanceBetweenUs - b.distanceBetweenUs;
    });

    return updatedShopOrder;
  }

  static sortContentfulLocationsByDistanceFromOrigin = (stores, searchResultLocation) => {
    const searchPoint = point(searchResultLocation);
    return stores
      .map(store => {
        const storePoint = point([store.geoLocation.lon, store.geoLocation.lat]);
        return {
          ...store,
          distance: distance(searchPoint, storePoint, { units: 'miles' }),
        };
      })
      .sort((a, b) => a.distance - b.distance);
  };

  /**
   * Validate if a size/variant contains metafileds with store location inventory
   *
   * @param {object} variant a Variant object
   * @returns {boolean} boolean value
   */
  static isInventoryAvailableOnSize(variant) {
    let result = false;

    if (variant?.metafields && Object.keys(variant?.metafields).length > 0) {
      result = true;
    } else if (variant) {
      Log.debug(
        `StoreLocationUtils - missing metafields with inventory data on Variant id ${variant?.id}`
      );
    }

    return result;
  }
}

export { StoreLocationUtils };
